import Phaser from 'phaser';
import type { GameScene } from './GameScene';
import type { TowerType } from '../../core/types';
import type { GameState } from '../../core/game';
import { BALANCE } from '../../config/balance';
import { TOWER_COLORS } from '../theme';
import { TOWER_NAMES, TOWER_TUTORIALS } from '../../config/displayNames';
import { getAudioService } from '../audio/AudioService';
import { getMusicService } from '../audio/MusicService';
import { openTowerGuide } from '../towerGuide';
import { clampFillLevel } from '../towerInfo';
import { openArenaWaveDialog } from '../arenaWaveDialog';
import {
  unlockedEnemyTypes,
  HUB_ARENA_ENEMY_TYPES,
  type HubArenaKind,
} from '../../config/testArena';
import { addAudioControl, addMusicButton } from '../audio/audioControls';
import { addFullscreenButton } from '../screenControls';
import { ProgressService } from '../../services/progressService';
import { saveStorage } from '../saveStorage';
import { levelPickerFor } from '../hubShared';
import { LEVELS, LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS } from '../../config/levels';
import {
  SHOP_ORDER,
  SHOP_TOWERS,
  WIN_GRANT_TOWERS,
  availableTowers,
  rosterThroughLevel,
  towersIntroducedAt,
  TUTORIAL_LEVEL_IDS,
  menuTowerTexture,
} from '../../config/unlocks';

/** Shop rows shown per page. */
const PER_PAGE = 5;

// ---------------------------------------------------------------------------
// Layout constants  (canvas = 1280 × 720)
// ---------------------------------------------------------------------------
/** HUD draw depth — above GameScene's deepest layer (depth 10). */
const DEPTH = 20;

/** Top bar height (px). */
const TOP_BAR_H = 50;

/** Right panel: left edge, width, and horizontal centre. */
const PANEL_X = 1070;
const PANEL_W = 210;
const PANEL_CX = PANEL_X + PANEL_W / 2; // 1175

/** Touch-target button height (≥44 px) — for Start Wave and selected-tower buttons. */
const BTN_H = 55;

/** localStorage flag — set once the one-time fast-forward explainer has shown. */
const FF_HINT_KEY = 'hambi.ffHintSeen';

/**
 * Game-registry key holding whether fast-forward is engaged. The registry is
 * session-scoped (survives scene restarts, resets on page reload), so toggling
 * 2× speed stays on as the player advances from one level to the next — the
 * button stays lit and the new level starts at 2×. A fresh page load begins at
 * normal speed.
 */
const FF_STATE_KEY = 'hambi.ffOn';

/** Shop row height (≥44 px touch target). */
const ROW_H = 64;

/**
 * Y-centre of the first shop row (Patty) and vertical stride between rows.
 *
 * E2E click coordinate for Patty row: (PANEL_CX=1175, SHOP_Y0=120).
 */
const SHOP_Y0 = 120;
const SHOP_STRIDE = 66;

/** Tower thumbnail display size within each row (px). */
const ICON_SIZE = 46;
/**
 * X-centre of the tower thumbnail icon within each row.
 * = PANEL_X + 6 (left inset) + ICON_SIZE/2 = 1070 + 6 + 23 = 1099
 */
const ICON_CX = PANEL_X + 6 + ICON_SIZE / 2; // 1099
/**
 * X-centre of the name/cost text block (right of the icon).
 * Midpoint of: [PANEL_X+6+ICON_SIZE+6 .. PANEL_X+PANEL_W-6]
 * = (1128 + 1274) / 2 = 1201
 */
const TEXT_CX = (PANEL_X + 6 + ICON_SIZE + 6 + (PANEL_X + PANEL_W - 6)) / 2; // 1201

/**
 * Y-centre of the Start Wave button.
 * = SHOP_Y0 + TOWER_TYPES.length * SHOP_STRIDE + 22
 * = 120 + 5*66 + 22 = 472
 *
 * E2E click coordinate for Start Wave: (PANEL_CX=1175, WAVE_Y=472).
 */
const WAVE_Y = SHOP_Y0 + PER_PAGE * SHOP_STRIDE + 22; // 472

/** Y-centre of the slim page-control strip (between the last row and Start Wave). */
const PAGE_CTRL_Y = SHOP_Y0 + (PER_PAGE - 1) * SHOP_STRIDE + ROW_H / 2 + 14; // ≈430

/**
 * Top of the selected-tower panel (px).
 * = WAVE_Y + ceil(BTN_H/2) + 10 = 472 + 28 + 10 = 510
 */
const SEL_TOP = WAVE_Y + Math.ceil(BTN_H / 2) + 10; // 510

/**
 * Arena top-bar tweaks. The arena swaps the 34px abandon-flag icon for a wider
 * text EXIT, so the whole right-hand control cluster slides left by half a slot
 * to keep the 40px pitch and stop EXIT colliding with the "?" guide button.
 * Campaign levels use shift 0 — play.spec.ts pins (1055,25) and (1255,25).
 */
const ARENA_BAR_SHIFT = 24;
const ARENA_EXIT_X = 1247;

// ---------------------------------------------------------------------------
// Internal type for a shop row record
// ---------------------------------------------------------------------------
interface ShopRow {
  /** Fixed slot index (0..PER_PAGE-1); its tower is repopulated per page. */
  slotIndex: number;
  /** Tower shown in this slot on the current page, or null when the slot is empty. */
  type: TowerType | null;
  cost: number;
  /** Full-row interactive background rectangle. */
  rowBg: Phaser.GameObjects.Rectangle;
  /** Tower thumbnail (texture = tower-<type>-0). */
  icon: Phaser.GameObjects.Image;
  nameText: Phaser.GameObjects.Text;
  costText: Phaser.GameObjects.Text;
}

// ---------------------------------------------------------------------------
// HudScene
// ---------------------------------------------------------------------------

/**
 * HudScene — runs as a parallel scene on top of GameScene.
 *
 * Visual tower shop: icon-left rows on a wooden-board panel background.
 *  - Stats bar: cash / lives / score / wave counter (refreshed via setText each frame).
 *  - Tower shop: one icon-left row per tower type; dims when player can't afford it;
 *    highlights the pending type; tap sets gs.pendingTowerType.
 *  - Start Wave button: enabled only during 'building' phase; dispatches startNextWave.
 *  - Selected-tower panel: visible when gs.selectedTowerId != null; shows tower art,
 *    name/tier, Upgrade (with cost) and Sell buttons.
 *
 * No per-frame object creation — all game objects are built once in create();
 * only setText / setAlpha / setVisible / setStrokeStyle / setTexture in update().
 */
export class HudScene extends Phaser.Scene {
  // Reference to the sibling GameScene (set in create after it has been registered)
  private gs!: GameScene;

  // ── Stat texts ──────────────────────────────────────────────────────────
  private cashText!: Phaser.GameObjects.Text;
  private livesText!: Phaser.GameObjects.Text;
  private scoreText!: Phaser.GameObjects.Text;
  private waveText!: Phaser.GameObjects.Text;

  // ── Shop ────────────────────────────────────────────────────────────────
  private shopRows: ShopRow[] = [];
  /** "PLACEMENT SEALED" banner shown while Emperor Pepro's decree holds. */
  private sealText!: Phaser.GameObjects.Text;
  /** "Only one Ultra Hammy at a time" — shown for a moment when his row is tapped. */
  private uniqueText!: Phaser.GameObjects.Text;
  private uniqueTextTimer: Phaser.Time.TimerEvent | null = null;
  private page = 0;
  /** Towers this level's shop shows — the equipped loadout (≤10), set in create(). */
  private availableTypes: TowerType[] = SHOP_ORDER;
  /**
   * Every tower the player owns/has earned (the uncapped roster). The build bar
   * is limited to the equipped loadout, but the "?" Tower Guide lists the whole
   * collection so players can review towers they own but haven't equipped.
   */
  private fullRoster: TowerType[] = SHOP_ORDER;
  private pageCount = 1;
  /** Scene-data captured in init(). */
  private _levelId: number = LEVELS[0].id;
  private _debug = false;
  /**
   * Test Arena mode. Distinct from _debug: debug hands out the FULL roster,
   * whereas the arena deliberately shows only what the player has unlocked —
   * it is a sandbox for your towers, not a preview of everyone's.
   */
  private _arena = false;
  /** The Moon Arena: The Moon's roster only, plus the Friendly toggle. */
  /** Which Hub arena this is (The Moon's or an Origin chapter's), if any. */
  private _hubArena: HubArenaKind | null = null;
  /** The Moon's Peaceful toggle is on for this run (a green tag in the bar). */
  private _peaceful = false;
  /** Tutorial modal game objects (destroyed together on dismiss). */
  private tutorialObjects: Phaser.GameObjects.GameObject[] = [];
  /** Auto-open the tower guide after create() (used by the ?debug gallery). */
  private _help = false;
  private prevBtn!: Phaser.GameObjects.Text;
  private nextBtn!: Phaser.GameObjects.Text;
  private pageLabel!: Phaser.GameObjects.Text;

  // ── Start Wave ──────────────────────────────────────────────────────────
  private waveBg!: Phaser.GameObjects.Rectangle;
  private waveLabel!: Phaser.GameObjects.Text;

  // ── Selected-tower panel ─────────────────────────────────────────────────
  /** All objects that belong to the selected-tower panel (toggled together). */
  private selPanel: Array<{ setVisible(v: boolean): void }> = [];
  /** Tower art sprite — texture is swapped each frame to match the selected tower. */
  private selArt!: Phaser.GameObjects.Image;
  private selTitle!: Phaser.GameObjects.Text;
  private upgBg!: Phaser.GameObjects.Rectangle;
  private upgLabel!: Phaser.GameObjects.Text;
  private sellBg!: Phaser.GameObjects.Rectangle;
  private sellLabel!: Phaser.GameObjects.Text;

  // ── Arena bulk tools (top bar) ────────────────────────────────────────────
  /** FILL ALL cluster — shown only while a shop tower is pending placement. */
  private fillCluster: Array<{ setVisible(v: boolean): void }> = [];
  /** Tier FILL ALL stamps (1–3), driven by the −/+ stepper. */
  private fillLevel = 1;
  private fillLevelText!: Phaser.GameObjects.Text;

  // ── Fast-forward toggle (top bar) ─────────────────────────────────────────
  private ffBg!: Phaser.GameObjects.Rectangle;
  private ffArrows!: Phaser.GameObjects.Graphics;

  // ── Pause toggle (top bar) ────────────────────────────────────────────────
  private pauseBg!: Phaser.GameObjects.Rectangle;
  private pauseGlyph!: Phaser.GameObjects.Graphics;

  constructor() {
    super({ key: 'HudScene' });
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  init(data: {
    levelId?: number;
    debug?: boolean;
    help?: boolean;
    arena?: boolean | HubArenaKind;
    peaceful?: boolean;
  }): void {
    this._levelId = data?.levelId ?? LEVELS[0].id;
    this._peaceful = data?.peaceful === true;
    this._debug = data?.debug ?? false;
    this._help = data?.help ?? false;
    this._arena = !!data?.arena;
    this._hubArena =
      data?.arena === 'moon' ||
      data?.arena === 'shadows' ||
      data?.arena === 'clouds' ||
      data?.arena === 'fungi'
        ? data.arena
        : null;
    // Phaser reuses the scene INSTANCE across scene.start(), so `page` survives
    // leaving the arena or finishing an episode. Every entry must open on the
    // first page of towers — otherwise the next run resumes on whatever page
    // was last browsed, which for a smaller roster is a page of empty slots.
    this.page = 0;
  }

  create(): void {
    // Cache the sibling GameScene.  HudScene.update() guards against the case
    // where this reference is obtained before GameScene.create() has run.
    this.gs = this.scene.get('GameScene') as GameScene;

    const tutorialType = this.resolveRosterAndTutorial();

    this.buildTopBar();
    this.buildRightPanel();

    if (tutorialType) this.showTowerTutorial(tutorialType);
    else if (this._help) this.showTowerGuide();
  }

  /**
   * Decide the shop roster and whether to show a first-start tutorial.
   *
   * Debug launches (?debug) show the full first-visit roster with all shop towers
   * treated as owned, and never persist or tutorialize. Real play marks the level
   * entered (so its tower appears this session), derives the cumulative roster
   * merged with `ownedTowers()`, and — on a genuine first start of a Chapter 1
   * episode — returns the introduced tower to tutorialize. Tutorials are
   * suppressed under VITE_E2E so the e2e specs drive an uninterrupted shop.
   * Win-granted towers are merged in and get the same modal once, on the next
   * episode entry.
   */
  private resolveRosterAndTutorial(): TowerType | null {
    if (this._debug) {
      this.availableTypes = rosterThroughLevel(this._levelId, [
        ...SHOP_TOWERS,
        ...WIN_GRANT_TOWERS,
      ]);
      this.fullRoster = this.availableTypes;
      this.pageCount = Math.max(1, Math.ceil(this.availableTypes.length / PER_PAGE));
      return null;
    }
    // The arena shows the player's real unlocked roster — same derivation as
    // normal play, but it never marks itself entered (a sandbox visit must not
    // move progression) and never tutorializes. Unlike an episode, the shop is
    // NOT capped to the ≤10 equipped loadout: the sandbox exists to test
    // everything you own, so the whole owned roster goes on the shelf.
    if (this._arena) {
      try {
        const progress = new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
        const roster = availableTowers(progress.enteredLevels(), progress.unlockedExtras());
        // Fall back to the opening roster, NOT rosterThroughLevel(_levelId) —
        // the arena's id is 900 and would resolve to "every tower in the game".
        const base = roster.length ? roster : rosterThroughLevel(LEVELS[0].id);
        this.fullRoster = base;
        this.availableTypes = base;
      } catch (err) {
        console.warn('[HudScene] progression read failed', err);
        this.availableTypes = rosterThroughLevel(LEVELS[0].id);
        this.fullRoster = this.availableTypes;
      }
      this.pageCount = Math.max(1, Math.ceil(this.availableTypes.length / PER_PAGE));
      return null;
    }
    let tutorial: TowerType | null = null;
    try {
      const progress = new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
      const firstStart = !progress.hasEntered(this._levelId);
      progress.markEntered(this._levelId);
      const roster = availableTowers(progress.enteredLevels(), progress.unlockedExtras());
      // The episode fields only the equipped loadout (≤10) — the player's Equip
      // choice, or a canonical-order default when they've never curated one. The
      // "?" guide, though, lists the full owned roster (`base`).
      const base = roster.length ? roster : rosterThroughLevel(this._levelId);
      this.fullRoster = base;
      this.availableTypes = progress.equippedTowers(base);
      if (firstStart && !import.meta.env.VITE_E2E && TUTORIAL_LEVEL_IDS.has(this._levelId)) {
        tutorial = towersIntroducedAt(this._levelId)[0] ?? null;
      }
      // Win-granted towers (the Wizard Tower) reuse the same unlock-intro modal
      // the first time the player enters any level after earning them.
      if (!tutorial && !import.meta.env.VITE_E2E) {
        const unseen = progress.unseenGrantedTower();
        if (unseen) {
          progress.markGrantedSeen(unseen);
          tutorial = unseen;
        }
      }
    } catch (err) {
      console.warn('[HudScene] progression read failed', err);
      this.availableTypes = rosterThroughLevel(this._levelId);
      this.fullRoster = this.availableTypes;
    }
    this.pageCount = Math.max(1, Math.ceil(this.availableTypes.length / PER_PAGE));
    return tutorial;
  }

  // ---------------------------------------------------------------------------
  // Builder helpers
  // ---------------------------------------------------------------------------

  private buildTopBar(): void {
    // Semi-transparent black strip behind stats
    this.add.rectangle(640, TOP_BAR_H / 2, 1280, TOP_BAR_H, 0x000000, 175).setDepth(DEPTH);

    const style: Phaser.Types.GameObjects.Text.TextStyle = {
      fontSize: '20px',
      color: '#ffffff',
    };
    const y = 14;

    // Level name (left) — the parody episode title for the active level.
    // Capped to a reserved width so long titles (e.g. "Return of the Golden
    // Nugget") can't bleed into the Cash readout; longer names scale down.
    const MAX_TITLE_W = 296; // leaves a gap before Cash at x=330
    const levelName = this.gs?.level?.name ?? '';
    const levelText = this.add
      .text(16, y + 2, levelName, { fontSize: '17px', color: '#f5c518', fontStyle: 'bold' })
      .setDepth(DEPTH + 1);
    if (levelText.width > MAX_TITLE_W) levelText.setScale(MAX_TITLE_W / levelText.width);

    this.cashText = this.add.text(330, y, 'Cash: $0', style).setDepth(DEPTH + 1);
    this.livesText = this.add.text(520, y, 'Lives: 0', style).setDepth(DEPTH + 1);
    this.scoreText = this.add.text(670, y, 'Score: 0', style).setDepth(DEPTH + 1);
    this.waveText = this.add.text(850, y, 'Build Phase', style).setDepth(DEPTH + 1);
    // Peaceful: a small green tag under the phase label's row, at the bar's
    // bottom edge, so the mode is never in doubt mid-run.
    if (this._peaceful) {
      this.add
        .text(850, TOP_BAR_H - 2, 'PEACEFUL · −30 coins', {
          fontSize: '11px',
          color: '#7ed67e',
          fontStyle: 'bold',
        })
        .setOrigin(0, 1)
        .setDepth(DEPTH + 1)
        .setName('hud-peaceful-tag');
    }
    // The arena keeps no score and charges nothing, so both readouts would be
    // permanent zeroes. Lives stay — a leak still costs one there. Hiding them
    // frees the left half of the bar, so Lives and the phase label slide left
    // into the gap; at x=850 the phase label would otherwise run under the
    // pause button, which the arena shifts left (ARENA_BAR_SHIFT).
    if (this._arena) {
      this.cashText.setVisible(false);
      this.scoreText.setVisible(false);
      this.livesText.setX(330);
      this.waveText.setX(520);
    }

    // Top-right control cluster, 40px pitch: fast-forward · music · fullscreen ·
    // audio · "?" · abandon (a white-flag "give up" icon — e2e clicks it at
    // 1255,25; fast-forward at 1055,25).
    const cy = TOP_BAR_H / 2;
    // Pause sits just left of fast-forward so the two speed controls group
    // together. The GameScene instance is reused across levels, so force a
    // known (running) state on entry rather than inheriting a stale flag.
    // The arena's EXIT is a text button and needs more room than the 34px flag
    // icon it replaces, so the whole cluster slides left by one half-slot there.
    // Campaign levels keep shift=0 — the e2e specs pin (1055,25) and (1255,25).
    const shift = this._arena ? ARENA_BAR_SHIFT : 0;
    this.buildPauseButton(1015 - shift, cy);
    this.setPaused(false);
    this.buildFastForwardButton(1055 - shift, cy);
    // Carry the fast-forward preference into this level: keep it lit and start at
    // 2× if the player left it on last level. The debug gallery always starts
    // normal. This also forces GameScene.timeScale to a known state (the scene
    // instance is reused across levels and would otherwise retain a stale value).
    this.setFastForward(!this._debug && this.registry.get(FF_STATE_KEY) === true);
    addMusicButton(this, 1095 - shift, cy, getMusicService(this));
    addFullscreenButton(this, 1135 - shift, cy);
    addAudioControl(this, 1175 - shift, cy, getAudioService(this));

    // "?" — opens the tower guide (stats for every currently unlocked tower).
    const helpX = 1215 - shift;
    const helpBg = this.add
      .rectangle(helpX, cy, 34, 34, 0x1a3a7a)
      .setStrokeStyle(2, 0x4488cc)
      .setInteractive({ useHandCursor: true })
      .setDepth(DEPTH + 1);
    this.add
      .text(helpX, cy, '?', { fontSize: '20px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setDepth(DEPTH + 2);
    helpBg.on('pointerup', () => this.showTowerGuide());

    // Abandon — a white surrender flag on red. Instantly stop the run and
    // return to Level Select. No confirmation, by design.
    // In the arena the same slot is an explicit EXIT: there is no run to
    // surrender, so a white flag would be the wrong word for "leave the sandbox".
    const abandonBg = this.add
      .rectangle(this._arena ? ARENA_EXIT_X : 1255, cy, this._arena ? 58 : 34, 34, 0x7a1a1a)
      .setStrokeStyle(2, 0xcc4444)
      .setInteractive({ useHandCursor: true })
      .setDepth(DEPTH + 1);
    if (this._arena) {
      this.add
        .text(ARENA_EXIT_X, cy, 'EXIT', { fontSize: '15px', color: '#ffffff', fontStyle: 'bold' })
        .setOrigin(0.5)
        .setDepth(DEPTH + 2);
    } else {
      const flag = this.add.graphics().setDepth(DEPTH + 2);
      flag.lineStyle(2, 0xffffff, 1);
      flag.lineBetween(1250, cy - 9, 1250, cy + 10); // pole
      flag.fillStyle(0xffffff, 1);
      flag.fillTriangle(1251, cy - 9, 1263, cy - 5, 1251, cy - 1); // flag
    }
    abandonBg.on('pointerup', () => {
      this.scene.stop('GameScene');
      const picker = levelPickerFor(this.gs?.level);
      this.scene.start(picker.key, picker.data);
    });

    // Arena-only bulk tools: RESET ALL (always) and FILL ALL (while placing).
    if (this._arena) this.buildArenaBulkTools(cy);
  }

  /**
   * The arena's bulk build tools, in the top bar's free middle stretch.
   *
   * RESET ALL (red, always visible) deletes every placed tower — freeBuild
   * towers refund nothing, so a sell sweep IS a wipe. FILL ALL (blue) appears
   * only while a shop tower is pending placement; it stamps that tower onto
   * every empty plot at the level shown by the −/+ stepper beside it (Lv 1 up to
   * the pending tower's own ceiling, default 1), then leaves placement mode.
   * Visibility and the ceiling are driven per-frame from update(), same as the
   * rest of the HUD.
   */
  private buildArenaBulkTools(cy: number): void {
    const resetBg = this.add
      .rectangle(710, cy, 104, 34, 0x7a1a1a)
      .setStrokeStyle(2, 0xcc4444)
      .setInteractive({ useHandCursor: true })
      .setDepth(DEPTH + 1);
    this.add
      .text(710, cy, 'RESET ALL', { fontSize: '13px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setDepth(DEPTH + 2);
    resetBg.on('pointerup', () => this.resetAllTowers());

    const fillBg = this.add
      .rectangle(820, cy, 92, 34, 0x1a3a7a)
      .setStrokeStyle(2, 0x4488cc)
      .setInteractive({ useHandCursor: true })
      .setDepth(DEPTH + 1);
    const fillLabel = this.add
      .text(820, cy, 'FILL ALL', { fontSize: '13px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setDepth(DEPTH + 2);
    fillBg.on('pointerup', () => this.fillAllPlots());

    // The −/+ stepper sets which tier FILL ALL stamps. Its ceiling is the
    // PENDING tower's own — Lv 4 exists only for a tower that has a tier-4
    // evolution AND has been Evolved, because the sim refuses the fourth upgrade
    // for anyone else and the stepper must not offer a level that quietly does
    // nothing. Big glyphs and
    // padded hit areas — the cluster must still clear the pause button at
    // x=991 (1015 − ARENA_BAR_SHIFT) on its right.
    const mkStep = (x: number, glyph: string, delta: number): Phaser.GameObjects.Text => {
      const t = this.add
        .text(x, cy, glyph, {
          fontSize: '30px',
          color: '#ffffff',
          fontStyle: 'bold',
          padding: { x: 8, y: 6 },
        })
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true })
        .setDepth(DEPTH + 2);
      t.on('pointerup', () => {
        this.fillLevel = clampFillLevel(this.fillLevel + delta, this.pendingMaxTier());
        this.fillLevelText.setText(`Lv ${this.fillLevel}`);
      });
      return t;
    };
    const minus = mkStep(888, '−', -1);
    this.fillLevelText = this.add
      .text(922, cy, `Lv ${this.fillLevel}`, {
        fontSize: '14px',
        color: '#9fd8ff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(DEPTH + 2);
    const plus = mkStep(956, '+', 1);

    this.fillCluster = [fillBg, fillLabel, minus, this.fillLevelText, plus];
    this.fillCluster.forEach((o) => o.setVisible(false));
  }

  /**
   * Highest tier the tower awaiting placement can reach in this run — the sim's
   * own answer, so the stepper and the sim never disagree. Tier 3 while nothing
   * is pending, which keeps the ceiling at Lv 3 rather than flashing Lv 4.
   */
  private pendingMaxTier(): 3 | 4 {
    const sim = this.gs?.sim;
    const type = this.gs?.pendingTowerType;
    return sim && type ? sim.maxTierFor(type) : 3;
  }

  /** FILL ALL: stamp the pending tower onto every empty plot at fillLevel. */
  private fillAllPlots(): void {
    const sim = this.gs?.sim;
    const type = this.gs?.pendingTowerType;
    if (!sim || !type) return;
    this.fillLevel = clampFillLevel(this.fillLevel, sim.maxTierFor(type));
    for (const plot of sim.state.plots) {
      if (sim.state.towers.some((t) => t.plotId === plot.id)) continue;
      if (!sim.dispatch({ type: 'placeTower', towerType: type, plotId: plot.id }).ok) continue;
      // placeTower doesn't return the id — re-find the tower by its plot.
      const placed = sim.state.towers.find((t) => t.plotId === plot.id);
      for (let tier = 1; placed && tier < this.fillLevel; tier++) {
        sim.dispatch({ type: 'upgradeTower', towerId: placed.id });
      }
    }
    this.gs.pendingTowerType = null;
  }

  /** RESET ALL: delete every placed tower (arena sells refund nothing). */
  private resetAllTowers(): void {
    const sim = this.gs?.sim;
    if (!sim) return;
    for (const id of sim.state.towers.map((t) => t.id)) {
      sim.dispatch({ type: 'sellTower', towerId: id });
    }
    this.gs.selectedTowerId = null;
  }

  private buildRightPanel(): void {
    const panelH = 720 - TOP_BAR_H;
    const panelMidY = TOP_BAR_H + panelH / 2;

    // ── Wooden board background ──────────────────────────────────────────────
    this.add
      .image(PANEL_CX, panelMidY, 'ui-panel-wood')
      .setDisplaySize(PANEL_W, panelH)
      .setDepth(DEPTH);

    // Faint dark overlay for text legibility
    this.add.rectangle(PANEL_CX, panelMidY, PANEL_W, panelH, 0x000000, 60).setDepth(DEPTH + 0.5);

    // "TOWER SHOP" heading (gold, bold)
    this.add
      .text(PANEL_CX, 68, 'TOWER SHOP', {
        fontSize: '15px',
        color: '#f5c518',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(DEPTH + 1);

    // Imperial Decree banner — a stamped badge over the top of the shop rows
    // while placement is sealed (updateShop drives visibility + countdown).
    this.sealText = this.add
      .text(PANEL_CX, 92, '', {
        fontSize: '13px',
        color: '#ff5f4f',
        fontStyle: 'bold',
        backgroundColor: '#3a0d08',
        padding: { x: 8, y: 3 },
      })
      .setOrigin(0.5)
      .setDepth(DEPTH + 2)
      .setVisible(false);

    this.uniqueText = this.add
      .text(PANEL_CX, 92, '', {
        fontSize: '13px',
        color: '#ffd23f',
        fontStyle: 'bold',
        backgroundColor: '#2a1a3a',
        padding: { x: 8, y: 3 },
        align: 'center',
        wordWrap: { width: PANEL_W - 30 },
      })
      .setOrigin(0.5)
      .setDepth(DEPTH + 3)
      .setVisible(false)
      .setName('hud-unique-text');

    this.buildShopRows();
    this.buildWaveButton();
    this.buildSelPanel();
  }

  private buildShopRows(): void {
    // Build PER_PAGE persistent slots once; their tower is repopulated per page
    // (no per-frame or per-page-flip object creation beyond property setters).
    this.shopRows = Array.from({ length: PER_PAGE }, (_, i): ShopRow => {
      const y = SHOP_Y0 + i * SHOP_STRIDE;

      const rowBg = this.add
        .rectangle(PANEL_CX, y, PANEL_W - 12, ROW_H, 0xffffff)
        .setStrokeStyle(2, 0xffffff)
        .setInteractive({ useHandCursor: true })
        .setDepth(DEPTH + 1);

      const icon = this.add
        .image(ICON_CX, y, 'tower-patty-1')
        .setDisplaySize(ICON_SIZE, ICON_SIZE)
        .setDepth(DEPTH + 2);

      // The name normally sits above centre to leave room for the cost at y+12.
      // The arena hides costs, so it centres on the row instead of floating high.
      const nameText = this.add
        .text(TEXT_CX, this._arena ? y : y - 10, '', {
          fontSize: '12px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(DEPTH + 2);

      const costText = this.add
        .text(TEXT_CX, y + 12, '', {
          fontSize: '12px',
          color: '#f5c518',
        })
        .setOrigin(0.5)
        .setDepth(DEPTH + 2);

      const slot: ShopRow = { slotIndex: i, type: null, cost: 0, rowBg, icon, nameText, costText };

      // Enter placement mode on tap (only for a populated, affordable slot).
      // Read the live row via this.shopRows[i], NOT the closed-over `slot`:
      // `slot` is an object literal initialised `{ type: null, cost: 0 }` and
      // Rollup's production build constant-folds `slot.type`/`slot.cost` to those
      // init values (it doesn't track the later `slot.type = …` mutation made via
      // this.shopRows in refreshPage). That folds the condition to always-false,
      // so the whole block was tree-shaken away — placement worked in dev but was
      // dead in built bundles. Indexing this.shopRows is opaque to that fold.
      rowBg.on('pointerup', () => {
        const row = this.shopRows[slot.slotIndex];
        if (!row.type || !this.gs?.sim) return;
        const s = this.gs.sim.state;
        // No placement mode while the Imperial Decree seals the field.
        if (s.placementLockedFor > 0) return;
        // Ultra Hammy: one at a time — say so instead of silently refusing.
        if (this.gs.sim.placementBlocked(row.type)) {
          this.showUniqueRefusal(row.type);
          return;
        }
        if (s.cash >= row.cost) {
          this.gs.pendingTowerType = row.type;
        }
      });

      return slot;
    });

    this.buildPageControls();
    this.refreshPage();
  }

  private buildPageControls(): void {
    // Big glyph + padding: the padding inflates the text bounds, which is what
    // Phaser uses as the pointer hit area — without it the tap target is just
    // the few-pixel-wide glyph, hopeless on touch screens.
    this.prevBtn = this.add
      .text(PANEL_X + 24, PAGE_CTRL_Y, '‹', {
        fontSize: '34px',
        color: '#ffffff',
        fontStyle: 'bold',
        padding: { x: 14, y: 8 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .setDepth(DEPTH + 2);
    this.nextBtn = this.add
      .text(PANEL_X + PANEL_W - 24, PAGE_CTRL_Y, '›', {
        fontSize: '34px',
        color: '#ffffff',
        fontStyle: 'bold',
        padding: { x: 14, y: 8 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .setDepth(DEPTH + 2);
    this.pageLabel = this.add
      .text(PANEL_CX, PAGE_CTRL_Y, '', { fontSize: '13px', color: '#f5c518' })
      .setOrigin(0.5)
      .setDepth(DEPTH + 2);

    this.prevBtn.on('pointerup', () => {
      if (this.page > 0) {
        this.page--;
        this.refreshPage();
      }
    });
    this.nextBtn.on('pointerup', () => {
      if (this.page < this.pageCount - 1) {
        this.page++;
        this.refreshPage();
      }
    });
  }

  /** Repopulate the row slots from the current page and refresh the controls. */
  private refreshPage(): void {
    const start = this.page * PER_PAGE;
    for (const slot of this.shopRows) {
      const type = this.availableTypes[start + slot.slotIndex] ?? null;
      slot.type = type;
      const has = type != null;
      slot.rowBg.setVisible(has);
      slot.icon.setVisible(has);
      slot.nameText.setVisible(has);
      // Arena towers are free, so the price is meaningless noise — hide it.
      slot.costText.setVisible(has && !this._arena);
      if (type) {
        slot.cost = this._arena ? 0 : BALANCE.towers[type].tier1.cost;
        slot.rowBg.setFillStyle(TOWER_COLORS[type]);
        slot.icon.setTexture(menuTowerTexture(type)).setDisplaySize(ICON_SIZE, ICON_SIZE);
        slot.nameText.setText(TOWER_NAMES[type]);
        slot.costText.setText(`$${slot.cost}`);
      }
    }
    this.updatePageControls();
  }

  private updatePageControls(): void {
    const multi = this.pageCount > 1;
    this.prevBtn.setVisible(multi).setAlpha(this.page > 0 ? 1 : 0.3);
    this.nextBtn.setVisible(multi).setAlpha(this.page < this.pageCount - 1 ? 1 : 0.3);
    this.pageLabel.setVisible(multi).setText(`${this.page + 1} / ${this.pageCount}`);
  }

  private buildWaveButton(): void {
    this.waveBg = this.add
      .rectangle(PANEL_CX, WAVE_Y, PANEL_W - 12, BTN_H, 0x1a6b1a)
      .setStrokeStyle(2, 0x4caf50)
      .setInteractive({ useHandCursor: true })
      .setDepth(DEPTH + 1);

    this.waveLabel = this.add
      .text(PANEL_CX, WAVE_Y, 'START WAVE', {
        fontSize: '15px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(DEPTH + 2);

    this.waveBg.on('pointerup', () => {
      if (this._arena) {
        this.onArenaWaveButton();
        return;
      }
      if (this.gs?.sim && this.gs.sim.state.canStartNextWave) {
        this.gs.sim.dispatch({ type: 'startNextWave' });
      }
    });
  }

  /**
   * Arena wave button: opens the wave dialog while the pitch is clear, and acts
   * as Clear Wave while one is in flight.
   */
  private onArenaWaveButton(): void {
    const sim = this.gs?.sim;
    if (!sim) return;
    if (sim.state.arenaWaveActive) {
      // Clearing mid-wave abandons any Shrekbook challenge attempt — the
      // remaining Shrekbruits are wiped, not defeated.
      this.gs?.resetArenaChallenge();
      sim.dispatch({ type: 'clearArena' });
      return;
    }
    const types = this._hubArena
      ? HUB_ARENA_ENEMY_TYPES[this._hubArena]
      : unlockedEnemyTypes((id) => this.arenaProgress().isUnlocked(id));
    // Pause the sim under the modal so a wave can't be walking while you pick.
    this.scene.pause('GameScene');
    const opened = openArenaWaveDialog(this, {
      types,
      // The Hub's arenas: Friendly, and "Disable boss abilities".
      friendly: this._hubArena !== null,
      bossToggle: this._hubArena !== null,
      // onClose fires on every teardown (including START), so the resume lives
      // there alone and onStart only has to send the wave.
      onStart: (entries, interval, mods) => {
        // Arm BEFORE the wave is sent: a qualifying wave (exactly 100
        // Shrekbruits and nothing else) starts the Shrekbook challenge, and
        // anything else disarms a previous attempt.
        this.gs?.armArenaChallenge(entries);
        sim.dispatch({
          type: 'startArenaWave',
          entries,
          interval,
          disableStun: mods.disableStun,
          disableFireSlow: mods.disableFireSlow,
          disableControl: mods.disableControl,
          disableTowerFire: mods.disableTowerFire,
          disableBossAbility: mods.disableBossAbility,
        });
      },
      onClose: () => this.scene.resume('GameScene'),
    });
    // The dialog declines to open with an empty roster or one already up — don't
    // strand GameScene paused behind a modal that never appeared.
    if (!opened) this.scene.resume('GameScene');
  }

  /** ProgressService for arena reads; falls back to "level 1 only" on failure. */
  private arenaProgress(): { isUnlocked: (id: number) => boolean } {
    try {
      const p = new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
      return { isUnlocked: (id: number) => p.isUnlocked(id) };
    } catch (err) {
      console.warn('[HudScene] progression read failed', err);
      return { isUnlocked: (id: number) => id === LEVELS[0].id };
    }
  }

  private buildSelPanel(): void {
    const top = SEL_TOP; // 510
    const SEL_BTN_H = 46;

    // Panel background box — sized to contain art, title, and both buttons.
    const selBg = this.add
      .rectangle(PANEL_CX, top + 96, PANEL_W - 10, 188, 0x1a1a33, 200)
      .setStrokeStyle(2, 0x6666cc)
      .setDepth(DEPTH + 1);

    // Tower art — texture is swapped in updateSelPanel(); starts as patty-0 (valid key)
    this.selArt = this.add
      .image(PANEL_CX, top + 22, 'tower-patty-1')
      .setDisplaySize(44, 44)
      .setDepth(DEPTH + 2);

    // Title: "TYPE — T1" (well clear of the upgrade button below)
    this.selTitle = this.add
      .text(PANEL_CX, top + 54, 'SELECTED', {
        fontSize: '13px',
        color: '#aaaaff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(DEPTH + 2);

    // Upgrade button
    this.upgBg = this.add
      .rectangle(PANEL_CX, top + 96, PANEL_W - 20, SEL_BTN_H, 0x1a3a7a)
      .setStrokeStyle(2, 0x4488cc)
      .setInteractive({ useHandCursor: true })
      .setDepth(DEPTH + 1);

    this.upgLabel = this.add
      .text(PANEL_CX, top + 96, 'UPGRADE', { fontSize: '14px', color: '#ffffff' })
      .setOrigin(0.5)
      .setDepth(DEPTH + 2);

    this.upgBg.on('pointerup', () => {
      if (this.gs?.sim && this.gs.selectedTowerId != null) {
        this.gs.sim.dispatch({ type: 'upgradeTower', towerId: this.gs.selectedTowerId });
      }
    });

    // Sell button
    this.sellBg = this.add
      .rectangle(PANEL_CX, top + 150, PANEL_W - 20, SEL_BTN_H, 0x7a1a1a)
      .setStrokeStyle(2, 0xcc4444)
      .setInteractive({ useHandCursor: true })
      .setDepth(DEPTH + 1);

    this.sellLabel = this.add
      .text(PANEL_CX, top + 150, 'SELL', { fontSize: '14px', color: '#ffffff' })
      .setOrigin(0.5)
      .setDepth(DEPTH + 2);

    this.sellBg.on('pointerup', () => {
      if (this.gs?.sim && this.gs.selectedTowerId != null) {
        this.gs.sim.dispatch({ type: 'sellTower', towerId: this.gs.selectedTowerId });
        this.gs.selectedTowerId = null;
      }
    });

    // Collect all panel objects for visibility toggling
    this.selPanel = [
      selBg,
      this.selArt,
      this.selTitle,
      this.upgBg,
      this.upgLabel,
      this.sellBg,
      this.sellLabel,
    ];

    // Hidden until a tower is selected
    this.selPanel.forEach((o) => o.setVisible(false));
  }

  // ---------------------------------------------------------------------------
  // First-start tutorial modal
  // ---------------------------------------------------------------------------

  /**
   * First-start tutorial: a centered modal introducing the newly unlocked tower.
   * Renders in HudScene (the top scene) at high depth, so its full-screen scrim
   * sits above the shop and — with Phaser's default topOnly input — swallows all
   * clicks behind it. Pauses GameScene while open; "Got it!" resumes it.
   */
  private showTowerTutorial(type: TowerType): void {
    const W = 1280;
    const H = 720;
    const D = 100; // above all HUD elements (deepest ~DEPTH+3 = 23)
    this.scene.pause('GameScene');

    const objs = this.tutorialObjects;
    objs.push(
      this.add
        .rectangle(W / 2, H / 2, W, H, 0x000000, 0.72)
        .setInteractive()
        .setDepth(D),
    );
    objs.push(
      this.add
        .rectangle(W / 2, H / 2, 560, 340, 0x1a1726, 1)
        .setStrokeStyle(3, 0xf5c518)
        .setDepth(D + 1),
    );
    objs.push(
      this.add
        .text(W / 2, H / 2 - 132, 'NEW TOWER UNLOCKED', {
          fontSize: '20px',
          color: '#f5c518',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    const artKey = this.textures.exists(menuTowerTexture(type))
      ? menuTowerTexture(type)
      : 'tower-patty-1';
    objs.push(
      this.add
        .image(W / 2, H / 2 - 60, artKey)
        .setDisplaySize(96, 96)
        .setDepth(D + 2),
    );
    objs.push(
      this.add
        .text(W / 2, H / 2 + 4, TOWER_NAMES[type], {
          fontSize: '26px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    objs.push(
      this.add
        .text(W / 2, H / 2 + 58, TOWER_TUTORIALS[type], {
          fontSize: '16px',
          color: '#cdd6dd',
          align: 'center',
          wordWrap: { width: 480 },
          lineSpacing: 4,
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    const btn = this.add
      .rectangle(W / 2, H / 2 + 132, 200, 52, 0x1a6b1a)
      .setStrokeStyle(2, 0x4caf50)
      .setInteractive({ useHandCursor: true })
      .setDepth(D + 2);
    objs.push(btn);
    objs.push(
      this.add
        .text(W / 2, H / 2 + 132, 'Got it!', {
          fontSize: '20px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 3),
    );
    btn.on('pointerup', () => {
      objs.forEach((o) => o.destroy());
      this.tutorialObjects = [];
      this.scene.resume('GameScene');
    });
  }

  // ---------------------------------------------------------------------------
  // Fast-forward toggle
  // ---------------------------------------------------------------------------

  /**
   * Fast-forward toggle — a double-arrow "⏩" icon in the top-right cluster.
   * Enabled (lit gold) doubles GameScene.timeScale; disabled (dark slate)
   * restores normal speed. The very first click ever (per browser) opens a
   * one-time explainer modal instead of silently speeding up; dismissing it
   * engages fast-forward. Debug (?debug) launches never show the explainer,
   * matching the no-tutorialize rule for the gallery.
   */
  private buildFastForwardButton(x: number, y: number): void {
    this.ffBg = this.add
      .rectangle(x, y, 34, 34, 0x2a2a33)
      .setStrokeStyle(2, 0x8890a0)
      .setInteractive({ useHandCursor: true })
      .setDepth(DEPTH + 1);
    this.ffArrows = this.add.graphics().setDepth(DEPTH + 2);
    this.drawFfArrows(0xffffff);

    this.ffBg.on('pointerup', () => {
      if (this.tutorialObjects.length > 0) return; // a modal is already up
      if (!this._debug && !this.hasSeenFfHint()) {
        this.markFfHintSeen();
        this.showFfHint();
        return; // fast-forward engages when the explainer is dismissed
      }
      this.setFastForward(this.gs.timeScale === 1);
    });
  }

  /** Draw the double right-chevron icon centred on the toggle. */
  private drawFfArrows(color: number): void {
    const { x, y } = this.ffBg;
    this.ffArrows.clear();
    this.ffArrows.fillStyle(color, 1);
    this.ffArrows.fillTriangle(x - 11, y - 8, x - 11, y + 8, x - 1, y);
    this.ffArrows.fillTriangle(x, y - 8, x, y + 8, x + 10, y);
  }

  /** Apply the toggle: 2× sim speed when on, 1× when off, and restyle the button. */
  private setFastForward(on: boolean): void {
    this.gs.timeScale = on ? 2 : 1;
    // Remember the choice for the next level (session-scoped; see FF_STATE_KEY).
    this.registry.set(FF_STATE_KEY, on);
    this.ffBg.setFillStyle(on ? 0x7a5a1a : 0x2a2a33);
    this.ffBg.setStrokeStyle(2, on ? 0xf5c518 : 0x8890a0);
    this.drawFfArrows(on ? 0xf5c518 : 0xffffff);
  }

  /**
   * Pause toggle — a "⏸" bars icon in the top-right cluster. Engaged (lit gold,
   * showing a "▶" resume triangle) freezes the sim via GameScene.paused;
   * disengaged (dark slate, showing the two pause bars) resumes it. Clicking
   * the button flips the state.
   */
  private buildPauseButton(x: number, y: number): void {
    this.pauseBg = this.add
      .rectangle(x, y, 34, 34, 0x2a2a33)
      .setStrokeStyle(2, 0x8890a0)
      .setInteractive({ useHandCursor: true })
      .setDepth(DEPTH + 1);
    this.pauseGlyph = this.add.graphics().setDepth(DEPTH + 2);
    this.drawPauseGlyph();

    this.pauseBg.on('pointerup', () => {
      if (this.tutorialObjects.length > 0) return; // a modal is already up
      this.setPaused(!this.gs.paused);
    });
  }

  /** Draw the glyph: a ▶ resume triangle while paused, else the ⏸ two bars. */
  private drawPauseGlyph(): void {
    const { x, y } = this.pauseBg;
    const paused = this.gs?.paused ?? false;
    const color = paused ? 0xf5c518 : 0xffffff;
    this.pauseGlyph.clear();
    this.pauseGlyph.fillStyle(color, 1);
    if (paused) {
      this.pauseGlyph.fillTriangle(x - 7, y - 9, x - 7, y + 9, x + 9, y);
    } else {
      this.pauseGlyph.fillRect(x - 8, y - 9, 5, 18);
      this.pauseGlyph.fillRect(x + 3, y - 9, 5, 18);
    }
  }

  /** Apply the toggle: freeze/resume the sim and restyle the button. */
  private setPaused(on: boolean): void {
    this.gs.paused = on;
    this.pauseBg.setFillStyle(on ? 0x7a5a1a : 0x2a2a33);
    this.pauseBg.setStrokeStyle(2, on ? 0xf5c518 : 0x8890a0);
    this.drawPauseGlyph();
  }

  private hasSeenFfHint(): boolean {
    try {
      return saveStorage().getItem(FF_HINT_KEY) === '1';
    } catch {
      return true; // storage unavailable — never nag
    }
  }

  private markFfHintSeen(): void {
    try {
      saveStorage().setItem(FF_HINT_KEY, '1');
    } catch {
      /* storage unavailable — hint just won't persist */
    }
  }

  /**
   * One-time fast-forward explainer — same modal pattern as the tower tutorial:
   * full-screen scrim swallows clicks, GameScene pauses, "Got it!" resumes play
   * and engages fast-forward so the player immediately sees what it does.
   */
  private showFfHint(): void {
    const W = 1280;
    const H = 720;
    const D = 100; // above all HUD elements
    this.scene.pause('GameScene');

    const objs = this.tutorialObjects;
    objs.push(
      this.add
        .rectangle(W / 2, H / 2, W, H, 0x000000, 0.72)
        .setInteractive()
        .setDepth(D),
    );
    objs.push(
      this.add
        .rectangle(W / 2, H / 2, 520, 240, 0x1a1726, 1)
        .setStrokeStyle(3, 0xf5c518)
        .setDepth(D + 1),
    );
    objs.push(
      this.add
        .text(W / 2, H / 2 - 80, 'FAST FORWARD', {
          fontSize: '20px',
          color: '#f5c518',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    objs.push(
      this.add
        .text(
          W / 2,
          H / 2 - 15,
          'Doubles the game speed. The button lights up while fast forward ' +
            'is on — click it again to return to normal speed.',
          {
            fontSize: '16px',
            color: '#cdd6dd',
            align: 'center',
            wordWrap: { width: 440 },
            lineSpacing: 4,
          },
        )
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    const btn = this.add
      .rectangle(W / 2, H / 2 + 70, 200, 52, 0x1a6b1a)
      .setStrokeStyle(2, 0x4caf50)
      .setInteractive({ useHandCursor: true })
      .setDepth(D + 2);
    objs.push(btn);
    objs.push(
      this.add
        .text(W / 2, H / 2 + 70, 'Got it!', {
          fontSize: '20px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 3),
    );
    btn.on('pointerup', () => {
      objs.forEach((o) => o.destroy());
      this.tutorialObjects = [];
      this.scene.resume('GameScene');
      this.setFastForward(true);
    });
  }

  // ---------------------------------------------------------------------------
  // "?" tower guide modal
  // ---------------------------------------------------------------------------

  /**
   * Tower guide for the full owned roster (shared modal — see render/towerGuide).
   * Shows every tower the player owns/has earned, not just the equipped loadout,
   * so the build bar's 10-slot cap doesn't hide towers from the reference guide.
   * Pauses GameScene while open; never stacks over the tutorial or itself.
   */
  private showTowerGuide(): void {
    if (this.tutorialObjects.length > 0) return;
    const opened = openTowerGuide(this, this.fullRoster, {
      onClose: () => this.scene.resume('GameScene'),
    });
    if (opened) this.scene.pause('GameScene');
  }

  // ---------------------------------------------------------------------------
  // Hit-test — used by InputController to block world taps on the HUD
  // ---------------------------------------------------------------------------

  /**
   * Returns true when screen-space point (x, y) falls within any HUD region.
   * Uses the same layout constants that position the panels, so there is a
   * single source of truth.
   *
   *  Top bar    : full width, y = 0 .. TOP_BAR_H
   *  Right panel: x = PANEL_X .. (PANEL_X + PANEL_W), y = TOP_BAR_H .. 720
   */
  isPointerOverHud(x: number, y: number): boolean {
    if (y < TOP_BAR_H) return true;
    if (x >= PANEL_X) return true;
    return false;
  }

  // ---------------------------------------------------------------------------
  // Game loop
  // ---------------------------------------------------------------------------

  override update(): void {
    // Guard: GameScene may not have called create() yet
    if (!this.gs || !this.gs.sim) return;

    const state: GameState = this.gs.sim.state;

    this.updateStats(state);
    this.updateShop(state);
    this.updateWaveButton(state);
    this.updateSelPanel(state);

    // FILL ALL rides the placement mode: visible exactly while a shop tower
    // is waiting for a plot tap (arena only — the cluster exists nowhere else).
    if (this._arena) {
      const placing = this.gs.pendingTowerType != null;
      this.fillCluster.forEach((o) => o.setVisible(placing));
      // Selecting a tower with no tier-4 evolution while Lv 4 is chosen pulls the
      // stepper back to Lv 3 — the level follows the tower, not the other way round.
      if (placing) {
        const clamped = clampFillLevel(this.fillLevel, this.pendingMaxTier());
        if (clamped !== this.fillLevel) {
          this.fillLevel = clamped;
          this.fillLevelText.setText(`Lv ${this.fillLevel}`);
        }
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Per-frame update sub-routines  (no object creation — only property setters)
  // ---------------------------------------------------------------------------

  private updateStats(state: GameState): void {
    this.cashText.setText(`Cash: $${state.cash}`);
    this.livesText.setText(`Lives: ${state.lives}`);
    this.scoreText.setText(`Score: ${state.score}`);

    let waveStr: string;
    if (this._arena) {
      // "Wave n/0" would be nonsense here — the arena has no wave list. Report
      // what the player is actually watching: how many enemies are on the pitch.
      waveStr = state.arenaWaveActive ? `Enemies: ${state.enemies.length}` : 'Build Phase';
    } else if (state.phase === 'building' && state.waveIndex === -1) {
      waveStr = 'Build Phase';
    } else {
      waveStr = `Wave ${state.waveIndex + 1}/${state.totalWaves}`;
    }
    this.waveText.setText(waveStr);
  }

  /** Flash "Only one <tower> at a time" over the shop for two seconds. */
  private showUniqueRefusal(type: TowerType): void {
    this.uniqueText.setText(`Only one ${TOWER_NAMES[type]} at a time`).setVisible(true);
    this.uniqueTextTimer?.remove();
    this.uniqueTextTimer = this.time.delayedCall(2000, () => {
      this.uniqueText.setVisible(false);
      this.uniqueTextTimer = null;
    });
  }

  private updateShop(state: Pick<GameState, 'cash' | 'placementLockedFor'>): void {
    // Emperor Pepro's Imperial Decree: placement is sealed — banner up, every
    // row dimmed regardless of cash so the shop reads "not now".
    const sealed = state.placementLockedFor > 0;
    this.sealText.setVisible(sealed);
    if (sealed) this.sealText.setText(`⛔ SEALED ${Math.ceil(state.placementLockedFor)}s`);
    for (const row of this.shopRows) {
      if (!row.type) continue; // empty slot on the last page — stays hidden
      const affordable =
        !sealed && state.cash >= row.cost && !this.gs.sim.placementBlocked(row.type);
      const pending = this.gs.pendingTowerType === row.type;

      // Dim the whole row when unaffordable; let the wood board show at ~70% when affordable
      const rowAlpha = affordable ? 0.8 : 0.25;
      const elemAlpha = affordable ? 1 : 0.4;

      row.rowBg.setAlpha(rowAlpha);
      row.icon.setAlpha(elemAlpha);
      row.nameText.setAlpha(elemAlpha);
      row.costText.setAlpha(elemAlpha);

      // Yellow highlight stroke when this type is pending placement
      row.rowBg.setStrokeStyle(3, pending ? 0xffff00 : 0xffffff);
    }
  }

  private updateWaveButton(
    state: Pick<GameState, 'canStartNextWave' | 'earlyBonus' | 'autoStartIn' | 'arenaWaveActive'>,
  ): void {
    // The arena's button is always live and has only two states: send a wave, or
    // wipe the one in flight. None of the campaign's bonus/countdown copy applies
    // (there is no wave list to count down to).
    if (this._arena) {
      const active = state.arenaWaveActive;
      this.waveBg.setAlpha(1).setFillStyle(active ? 0x7a1a1a : 0x1a6b1a);
      this.waveBg.setStrokeStyle(2, active ? 0xcc4444 : 0x4caf50);
      this.waveLabel.setAlpha(1).setText(active ? 'CLEAR WAVE' : 'START WAVE');
      return;
    }
    const enabled = state.canStartNextWave;
    this.waveBg.setAlpha(enabled ? 1 : 0.35);
    this.waveLabel.setAlpha(enabled ? 1 : 0.5);
    // Three states: pre-first-wave (untimed — START WAVE), between waves (live
    // auto-start countdown; tapping sends early for the decaying bonus), and
    // mid-wave (early concurrent send once the gate opens).
    if (enabled && state.autoStartIn != null) {
      const secs = Math.ceil(state.autoStartIn);
      this.waveLabel.setText(
        state.earlyBonus > 0
          ? `NEXT WAVE IN ${secs}s\nsend now for +$${state.earlyBonus}`
          : `NEXT WAVE IN ${secs}s`,
      );
    } else if (enabled && state.earlyBonus > 0) {
      this.waveLabel.setText(`SEND NEXT WAVE\nearly bonus +$${state.earlyBonus}`);
    } else {
      this.waveLabel.setText('START WAVE');
    }
  }

  private updateSelPanel(state: Pick<GameState, 'cash' | 'towers'>): void {
    const hasSel = this.gs.selectedTowerId != null;
    this.selPanel.forEach((o) => o.setVisible(hasSel));

    if (!hasSel) return;

    const tower = state.towers.find((t) => t.id === this.gs.selectedTowerId);
    if (!tower) return;

    const pair = BALANCE.towers[tower.type];
    // Evolved towers may climb one tier further (T4) — the sim owns that rule.
    const isMaxed = tower.tier >= this.gs.sim.maxTierFor(tower.type);
    // Cost of the NEXT upgrade (tier 1→2 uses tier2's cost … 3→4 uses tier4's),
    // so the 1-based tier indexes this 0-based list at tier - 1.
    const upgCost = isMaxed ? 0 : [pair.tier2, pair.tier3, pair.tier4!][tower.tier - 1].upgradeCost;
    // Arena upgrades are free, so only "already maxed" can block one.
    const canUpgrade = !isMaxed && (this._arena || state.cash >= upgCost);

    // Swap tower art to match selected tower's type and upgrade tier
    this.selArt.setTexture(`tower-${tower.type}-${tower.tier}`);
    this.selTitle.setText(`${TOWER_NAMES[tower.type]} — T${tower.tier}`);
    this.upgLabel.setText(isMaxed ? 'MAX TIER' : this._arena ? 'UPGRADE' : `UPGRADE  $${upgCost}`);
    this.upgBg.setAlpha(canUpgrade ? 1 : 0.4);
    // Sell is always available; show the coin refund (half of total spend).
    // In the arena towers cost nothing, so there is nothing to refund — the same
    // button reads DELETE and simply removes the tower.
    this.sellLabel.setText(this._arena ? 'DELETE' : `SELL  $${this.gs.sim.sellValue(tower.id)}`);
    this.sellBg.setAlpha(1);
  }
}
